import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { decimalToCents, formatCentsForInput } from '../../domain/finance';
import {
  PiBackspace,
  PiCopy,
  PiDivide,
  PiEquals,
  PiMinus,
  PiPercent,
  PiPlus,
  PiPlusMinus,
  PiX,
} from 'react-icons/pi';
import './styles.css';

export const DELETE_LONG_PRESS_MS = 550;

const convertToFloat = (value) =>
  String(value ?? '0')
    .replace(/[()]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

const toNumber = (value) => {
  const parsed = Number(convertToFloat(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value) => {
  const stringValue = String(value);

  const [rawInteger, decimalPart = ''] =
    stringValue.split('.');

  const sign =
    rawInteger.startsWith('-')
      ? '-'
      : '';

  const integer = rawInteger
    .replace('-', '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${sign}${integer}${decimalPart
    ? `,${decimalPart}`
    : ''
    }`;
};

const normalizeResult = (value) => {
  if (!Number.isFinite(value)) {
    return 'Erro';
  }

  /*
   * Limita imprecisões de ponto flutuante,
   * sem obrigar três casas decimais.
   */
  const rounded =
    Math.round(
      (value + Number.EPSILON) * 100000000
    ) / 100000000;

  return formatNumber(String(rounded));
};

const formatMoneyFieldValue = (value) => {
  const numericValue = toNumber(value);
  const cents = numericValue === null ? null : decimalToCents(numericValue);

  return formatCentsForInput(cents);
};

const executeOperation = (
  left,
  right,
  operator
) => {
  switch (operator) {
    case '+':
      return left + right;

    case '-':
      return left - right;

    case '×':
    case 'x':
    case '*':
      return left * right;

    case '÷':
    case '/':
      return right === 0
        ? null
        : left / right;

    default:
      return null;
  }
};

const CalculatorButton = ({
  label,
  className = '',
  children,
  ...props
}) => (
  <button
    type="button"
    className={`nf-calculator__key ${className}`.trim()}
    aria-label={label}
    {...props}
  >
    {children}
  </button>
);

const Calculator = ({
  onUseValue,
  onRequestClose,
}) => {
  const [num, setNum] = useState('0');
  const [accumulator, setAccumulator] =
    useState(null);
  const [operator, setOperator] =
    useState(null);

  const [waitingForOperand, setWaitingForOperand] =
    useState(false);

  const [history, setHistory] = useState('');
  const [justCalculated, setJustCalculated] =
    useState(false);

  const [copyMessage, setCopyMessage] =
    useState('');

  const [deletePressed, setDeletePressed] =
    useState(false);

  const holdTimeoutRef = useRef(null);
  const copyTimeoutRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const clear = useCallback(() => {
    setNum('0');
    setAccumulator(null);
    setOperator(null);
    setWaitingForOperand(false);
    setHistory('');
    setJustCalculated(false);
  }, []);

  const inputDigit = useCallback((digit) => {
    setNum((current) => {
      if (
        waitingForOperand ||
        justCalculated ||
        current === 'Erro'
      ) {
        setWaitingForOperand(false);
        setJustCalculated(false);

        return digit === ','
          ? '0,'
          : digit;
      }

      if (digit === ',') {
        return current.includes(',')
          ? current
          : `${current},`;
      }

      const next =
        current === '0'
          ? digit
          : `${current}${digit}`;

      return formatNumber(
        convertToFloat(next)
      );
    });
  }, [
    waitingForOperand,
    justCalculated,
  ]);

  const performPendingOperation =
    useCallback(() => {
      if (
        accumulator === null ||
        operator === null
      ) {
        return null;
      }

      const right = toNumber(num);

      if (right === null) {
        return null;
      }

      return executeOperation(
        accumulator,
        right,
        operator
      );
    }, [
      accumulator,
      num,
      operator,
    ]);

  const handleOperator =
    useCallback((nextOperator) => {
      if (nextOperator === '%') {
        return;
      }

      /*
       * Se o usuário apertar operadores em sequência,
       * apenas troca o operador pendente.
       *
       * Ex:
       * 10 + ×
       * vira 10 ×
       */
      if (
        waitingForOperand &&
        operator !== null
      ) {
        setOperator(nextOperator);

        setHistory((current) =>
          current.replace(
            /[+\-×÷]\s*$/,
            `${nextOperator}`
          )
        );

        return;
      }

      const currentValue = toNumber(num);

      if (currentValue === null) {
        return;
      }

      /*
       * Primeira operação.
       */
      if (
        accumulator === null ||
        operator === null
      ) {
        setAccumulator(currentValue);
        setOperator(nextOperator);
        setWaitingForOperand(true);
        setJustCalculated(false);

        setHistory(
          `${num} ${nextOperator}`
        );

        return;
      }

      /*
       * Já existe uma operação pendente.
       *
       * Ex:
       * 10 + 20 +
       *
       * calcula 30 e passa a continuar:
       * 30 +
       */
      const result =
        performPendingOperation();

      if (result === null) {
        setNum('Erro');
        setAccumulator(null);
        setOperator(null);
        setWaitingForOperand(true);
        setHistory('Operação inválida');
        return;
      }

      const formattedResult =
        normalizeResult(result);

      setNum(formattedResult);
      setAccumulator(result);
      setOperator(nextOperator);
      setWaitingForOperand(true);
      setJustCalculated(false);

      setHistory(
        `${formattedResult} ${nextOperator}`
      );
    }, [
      accumulator,
      num,
      operator,
      performPendingOperation,
      waitingForOperand,
    ]);

  const calculate = useCallback(() => {
    if (
      accumulator === null ||
      operator === null ||
      waitingForOperand
    ) {
      return;
    }

    const right = toNumber(num);

    if (right === null) {
      return;
    }

    const result = executeOperation(
      accumulator,
      right,
      operator
    );

    if (result === null) {
      setNum('Erro');
      setHistory(
        operator === '÷' && right === 0
          ? 'Não é possível dividir por zero'
          : 'Operação inválida'
      );

      setAccumulator(null);
      setOperator(null);
      setWaitingForOperand(true);
      setJustCalculated(true);

      return;
    }

    const formattedResult =
      normalizeResult(result);

    setHistory(
      `${formatNumber(String(accumulator))} ${operator} ${num} =`
    );

    setNum(formattedResult);

    setAccumulator(null);
    setOperator(null);
    setWaitingForOperand(false);
    setJustCalculated(true);
  }, [
    accumulator,
    num,
    operator,
    waitingForOperand,
  ]);

  const percentage = useCallback(() => {
    const currentValue = toNumber(num);

    if (currentValue === null) {
      return;
    }

    /*
     * Com operação pendente:
     *
     * 100 + 10%
     * => 10
     * => depois "=" resulta em 110
     *
     * 100 - 10%
     * idem.
     */
    if (
      accumulator !== null &&
      operator !== null
    ) {
      let percentageValue;

      if (
        operator === '+' ||
        operator === '-'
      ) {
        percentageValue =
          accumulator *
          (currentValue / 100);
      } else {
        percentageValue =
          currentValue / 100;
      }

      const formatted =
        normalizeResult(
          percentageValue
        );

      setNum(formatted);
      setWaitingForOperand(false);

      setHistory(
        `${formatNumber(
          String(accumulator)
        )} ${operator} ${num}%`
      );

      return;
    }

    const result =
      currentValue / 100;

    setNum(
      normalizeResult(result)
    );

    setHistory(`${num}% =`);
    setJustCalculated(true);
  }, [
    accumulator,
    num,
    operator,
  ]);

  const changeSign = useCallback(() => {
    setNum((current) => {
      if (
        current === '0' ||
        current === 'Erro'
      ) {
        return current;
      }

      return current.startsWith('-')
        ? current.slice(1)
        : `-${current}`;
    });
  }, []);

  const deleteLast = useCallback(() => {
    if (
      waitingForOperand ||
      justCalculated
    ) {
      return;
    }

    setNum((current) => {
      if (
        current === 'Erro' ||
        current.length <= 1
      ) {
        return '0';
      }

      const next =
        current.slice(0, -1);

      if (
        next === '-' ||
        next === ''
      ) {
        return '0';
      }

      /*
       * Preserva vírgula final durante edição.
       */
      if (next.endsWith(',')) {
        return next;
      }

      return formatNumber(
        convertToFloat(next)
      );
    });
  }, [
    waitingForOperand,
    justCalculated,
  ]);

  const cancelDeleteTimer =
    useCallback(() => {
      window.clearTimeout(
        holdTimeoutRef.current
      );

      holdTimeoutRef.current = null;
      setDeletePressed(false);
    }, []);

  const handleDeletePointerDown = (event) => {
    event.preventDefault();

    longPressTriggeredRef.current = false;
    setDeletePressed(true);

    holdTimeoutRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      clear();
      setDeletePressed(false);


    }, DELETE_LONG_PRESS_MS);
  };

  const handleDeleteClick = () => {
    if (
      longPressTriggeredRef.current
    ) {
      longPressTriggeredRef.current = false;
      return;
    }

    deleteLast();
  };

  const copyResult =
    useCallback(async () => {
      if (num === 'Erro') {
        return;
      }

      window.clearTimeout(
        copyTimeoutRef.current
      );

      try {
        await navigator.clipboard.writeText(
          num
        );

        setCopyMessage(
          'Resultado copiado'
        );
      } catch {
        setCopyMessage(
          'Não foi possível copiar'
        );
      }

      copyTimeoutRef.current =
        window.setTimeout(
          () => setCopyMessage(''),
          1200
        );
    }, [num]);

  useEffect(
    () => () => {
      window.clearTimeout(
        holdTimeoutRef.current
      );

      window.clearTimeout(
        copyTimeoutRef.current
      );
    },
    []
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (/^[0-9]$/.test(event.key)) {
        inputDigit(event.key);
      } else if (
        event.key === ',' ||
        event.key === '.'
      ) {
        inputDigit(',');
      } else if (
        ['+', '-', '*', '/'].includes(
          event.key
        )
      ) {
        handleOperator(
          event.key === '*'
            ? '×'
            : event.key === '/'
              ? '÷'
              : event.key
        );
      } else if (event.key === '%') {
        percentage();
      } else if (
        event.key === 'Enter' ||
        event.key === '='
      ) {
        event.preventDefault();
        calculate();
      } else if (
        event.key === 'Backspace'
      ) {
        deleteLast();
      } else if (
        event.key === 'Delete'
      ) {
        clear();
      } else if (
        event.key === 'Escape'
      ) {
        onRequestClose?.();
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
  }, [
    calculate,
    clear,
    deleteLast,
    handleOperator,
    inputDigit,
    onRequestClose,
    percentage,
  ]);

  return (
    <div className="nf-calculator">
      <div
        className="nf-calculator__display"
        aria-live="polite"
      >
        <div className="nf-calculator__display-meta">
          <div className="nf-calculator__expression">
            {copyMessage || history || ' '}
          </div>
          <button
            type="button"
            className="nf-calculator__copy"
            onClick={copyResult}
            aria-label="Copiar resultado"
          >
            <PiCopy aria-hidden="true" />
          </button>
        </div>

        <output
          className="nf-calculator__result"
          aria-label={`Resultado: ${num}`}
        >
          {num}
        </output>
      </div>

      <div className="nf-calculator__grid">
        <CalculatorButton
          label="Apagar último dígito. Pressione e segure para limpar tudo."
          className={`nf-calculator__key--utility ${deletePressed ? 'is-pressed' : ''
            }`}
          onPointerDown={handleDeletePointerDown}
          onPointerUp={cancelDeleteTimer}
          onPointerCancel={cancelDeleteTimer}
          onPointerLeave={cancelDeleteTimer}
          onContextMenu={(event) => event.preventDefault()}
          onClick={handleDeleteClick}
          title="Segure para limpar tudo"
        >
          <PiBackspace aria-hidden="true" />
        </CalculatorButton>

        <CalculatorButton
          label="Inverter sinal"
          className="nf-calculator__key--utility"
          onClick={changeSign}
        >
          <PiPlusMinus aria-hidden="true" />
        </CalculatorButton>

        <CalculatorButton
          label="Porcentagem"
          className="nf-calculator__key--utility"
          onClick={percentage}
        >
          <PiPercent aria-hidden="true" />
        </CalculatorButton>

        <CalculatorButton
          label="Dividir"
          className={`nf-calculator__key--operator ${operator === '÷'
            ? 'is-active'
            : ''
            }`}
          onClick={() =>
            handleOperator('÷')
          }
        >
          <PiDivide aria-hidden="true" />
        </CalculatorButton>

        {[7, 8, 9].map((digit) => (
          <CalculatorButton
            key={digit}
            label={String(digit)}
            onClick={() =>
              inputDigit(String(digit))
            }
          >
            {digit}
          </CalculatorButton>
        ))}

        <CalculatorButton
          label="Multiplicar"
          className={`nf-calculator__key--operator ${operator === '×'
            ? 'is-active'
            : ''
            }`}
          onClick={() =>
            handleOperator('×')
          }
        >
          <PiX aria-hidden="true" />
        </CalculatorButton>

        {[4, 5, 6].map((digit) => (
          <CalculatorButton
            key={digit}
            label={String(digit)}
            onClick={() =>
              inputDigit(String(digit))
            }
          >
            {digit}
          </CalculatorButton>
        ))}

        <CalculatorButton
          label="Subtrair"
          className={`nf-calculator__key--operator ${operator === '-'
            ? 'is-active'
            : ''
            }`}
          onClick={() =>
            handleOperator('-')
          }
        >
          <PiMinus aria-hidden="true" />
        </CalculatorButton>

        {[1, 2, 3].map((digit) => (
          <CalculatorButton
            key={digit}
            label={String(digit)}
            onClick={() =>
              inputDigit(String(digit))
            }
          >
            {digit}
          </CalculatorButton>
        ))}

        <CalculatorButton
          label="Somar"
          className={`nf-calculator__key--operator ${operator === '+'
            ? 'is-active'
            : ''
            }`}
          onClick={() =>
            handleOperator('+')
          }
        >
          <PiPlus aria-hidden="true" />
        </CalculatorButton>

        <CalculatorButton
          label="0"
          className="nf-calculator__key--zero"
          onClick={() => inputDigit('0')}
        >
          0
        </CalculatorButton>

        <CalculatorButton
          label="Vírgula decimal"
          onClick={() => inputDigit(',')}
        >
          ,
        </CalculatorButton>

        <CalculatorButton
          label="Calcular resultado"
          className="nf-calculator__key--equals"
          onClick={calculate}
        >
          <PiEquals aria-hidden="true" />
        </CalculatorButton>
      </div>

      <p className="nf-calculator__hint">
        Toque para apagar um dígito. Segure para limpar tudo.
      </p>

      {onUseValue && (
        <button
          type="button"
          className="nf-calculator__use-value"
          disabled={num === 'Erro'}
          onClick={() =>
            onUseValue(
              formatMoneyFieldValue(num)
            )
          }
        >
          Usar valor
        </button>
      )}
    </div>
  );
};

CalculatorButton.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Calculator.propTypes = {
  onUseValue: PropTypes.func,
  onRequestClose: PropTypes.func,
};

export default Calculator;
